import React from 'react';
import { ReportDimension } from 'core/report/ReportData';
import { faLink } from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import PermissionChecker from 'containers/PermissionChecker/PermissionChecker';
import { Permission } from 'core/auth/Permission';
import styles from './reportTable.module.scss';
import i18n from 'i18n';
import _ from 'lodash';
import { hasFuncs } from 'core/permission/PermissionDSL';
import IconWithTooltip from 'components/IconWithTooltip/IconWithTooltip';
import TableRowToolBar from 'containers/TableRowToolBar/TableRowToolBar';

export function getReportTableFormatters(onDimensionNameClick, onDateClick) {
  const nameFormatter = (
    columnData: any,
    rowData: any,
    rowIndex: number,
    formatExtraData: any
  ) => {
    if (columnData === undefined) {
      return <div />;
    }
    const dimension = formatExtraData.dimension;
    const isDateDimension =
      dimension === ReportDimension.MONTH ||
      dimension === ReportDimension.DAY ||
      dimension === ReportDimension.HOUR;
    const onClick = () => {
      if (isDateDimension) {
        onDateClick(columnData);
      } else {
        onDimensionNameClick(dimension, rowData.id);
      }
    };
    return (
      <div className={styles.nameCell}>
        <div className={styles.nameValue} onClick={onClick}>
          {columnData}
        </div>
      </div>
    );
  };

  const getLinkPathOfDimension = (
    dimension: ReportDimension,
    dimensionID: number | string,
    extra?: { [key: string]: any }
  ) => {
    switch (dimension) {
      case ReportDimension.ORDER:
        return `/orders/${dimensionID}/redirectToDetail`;
      case ReportDimension.L2_OBJECT:
        if (!extra) {
          return undefined;
        }
        return `/campaigns/${extra.l2ChannelId}/redirectToDetail?l1ObjectId=${extra.l1ObjectId}`;
      case ReportDimension.L1_OBJECT:
        return `/campaign-groups/${dimensionID}/redirectToDetail`;
      case ReportDimension.L3_CHANNEL:
        if (!extra) {
          return undefined;
        }
        return `/bindings/${dimensionID}/redirectToDetail?l1ObjectId=${extra.l1ObjectId}&campaignId=${extra.l2ChannelId}`;
      case ReportDimension.CREATIVE:
        return `/creatives/${dimensionID}/redirectToDetail`;
      case ReportDimension.CAMPAIGN:
      default:
        return undefined;
    }
  };

  const getLinkPermissionsOfDimension = (dimension: ReportDimension) => {
    switch (dimension) {
      case ReportDimension.CAMPAIGN:
      case ReportDimension.L2_OBJECT:
      case ReportDimension.L1_OBJECT:
        return [Permission.CAMPAIGN_READ, Permission.ORDER_READ];
      case ReportDimension.ORDER:
        return [Permission.ORDER_READ];
      case ReportDimension.CREATIVE:
        return [Permission.CREATIVE_READ];
      case ReportDimension.L3_CHANNEL:
        return [
          Permission.CAMPAIGN_READ,
          Permission.ORDER_READ,
          Permission.CREATIVE_READ
        ];
      default:
        return [];
    }
  };

  const textFormatter = (
    text: any,
    row: any,
    rowIndex: number,
    formatExtraData: any
  ) => {
    if (text === undefined) {
      return <div />;
    }
    const textDecorator = _.get(formatExtraData, 'textDecorator', _.identity);
    return <div className={styles.text}>{textDecorator(text)}</div>;
  };

  const tagFormatter = (tags) => {
    const renderTags = () => {
      return tags.map((tag) => {
        return <div key={tag}>{tag}</div>;
      });
    };
    return (
      <div className={styles.tagsContent}>
        {tags.length > 0
          ? renderTags()
          : i18n.t('campaignList.labels.emptyContent')}
      </div>
    );
  };

  const floatingEditBtnsFormatter = (
    cell: any,
    row: any,
    rowIndex: number,
    formatExtraData: any
  ): any => {
    const dimension = formatExtraData.dimension;
    const linkPath = getLinkPathOfDimension(dimension, row.id, row.extra);
    if (!linkPath) {
      return <div />;
    }
    return (
      <PermissionChecker
        permissionAware={hasFuncs(...getLinkPermissionsOfDimension(dimension))}
      >
        <TableRowToolBar className={styles.floatingEditArea}>
          <Link to={linkPath} target='_blank'>
            <IconWithTooltip
              icon={faLink}
              tooltipProps={{
                id: `reportTableLinkTip-${row.id}`,
                tooltip: i18n.t(`reportTable.labels.${dimension}LinkHint`)
              }}
            />
          </Link>
        </TableRowToolBar>
      </PermissionChecker>
    );
  };

  return {
    nameFormatter,
    textFormatter,
    tagFormatter,
    floatingEditBtnsFormatter
  };
}
